import {Button, Radio} from '@wandb/weave/components';
import * as React from 'react';
import {useRef} from 'react';
import {twMerge} from 'tailwind-merge';
import {v4 as uuidv4} from 'uuid';

import {type SettingOverrideCounts} from '../../../../state/views/workspaceSettings/settingOverrideUtils';
import {ClearSettingsRows} from '../components/ClearSettingsRow';
import {ResetSettingButton} from '../components/ResetSettingButton';
import {SettingLabel} from '../components/SettingLabel';
import {SettingSelect} from '../components/SettingSelect';
import {useClearSettingRows} from '../hooks/useClearSettingRows';
import {type ClearSettingFnType} from '../hooks/useSettingOverrides';
import {
  type DerivedPointVisualizationOption,
  FULL_FIDELITY_MODES,
  type FullFidelityMode,
  POINT_VISUALIZATION_OPTIONS,
  type PointVisualizationOptions,
  type SettingLevel,
} from '../types';
import {arePointAggregationSettingsActive} from '../utils';

/**
 * The radio group maps 1:1 onto `pointVisualizationMethod`:
 *   - 'sampling'          → pointVisualizationMethod: sampling
 *   - 'bucketing-gorilla' → pointVisualizationMethod: bucketing-gorilla
 * The second axis — `fullFidelityMode` (Band vs Line) — is controlled by the
 * "Full fidelity display" dropdown, shown only when Full fidelity is selected.
 */
type RadioValue = DerivedPointVisualizationOption;

const isRadioValue = (value: string): value is RadioValue =>
  value === POINT_VISUALIZATION_OPTIONS.Sampling ||
  value === POINT_VISUALIZATION_OPTIONS.BucketingGorilla;

/**
 * "Full fidelity display" options. Mirrors the sibling Smoothing dropdown
 * (SettingSelect): each option's `value` is the `fullFidelityMode` config value
 * itself, so onChange writes straight through with no mapping table.
 */
type FullFidelityDisplayOption = {value: FullFidelityMode; label: string};

const FULL_FIDELITY_DISPLAY_OPTIONS: FullFidelityDisplayOption[] = [
  {value: FULL_FIDELITY_MODES.Standard, label: 'Band'},
  {value: FULL_FIDELITY_MODES.MinMaxLast, label: 'Line'},
];

/**
 * Types of point visualization:
 * BucketingGorilla: this is new functionality using gorilla to bucket points without sampling. We ejected from Weave to do this for time, but long-term hope to move this to weave.

 * Sampling: this is the legacy functionality of doing sampling/bucketing clientside
 * SamplingByDefault: we need a way to differentiate downstream if a workspace has sampling
 * saved as a value or if we're just opting into it by default. If it's by default, we can
 * selectively override it to enroll organizations or users into bucketing to drive enrollment
 */

type LinePlotPointAggregationProps = {
  className?: string;
  clearSetting?: ClearSettingFnType;
  displayValues: {
    pointVisualizationMethod: DerivedPointVisualizationOption;
    fullFidelityMode?: FullFidelityMode;
  };
  numOverrides?: SettingOverrideCounts;
  onPointVisualizationMethodChange: (
    value: DerivedPointVisualizationOption
  ) => void;
  onFullFidelityModeChange?: (value: FullFidelityMode) => void;
  settingLevel: SettingLevel;
  values: {
    pointVisualizationMethod?: PointVisualizationOptions;
    fullFidelityMode?: FullFidelityMode;
  };
};

export const LinePlotPointAggregation = ({
  className,
  clearSetting,
  displayValues,
  numOverrides,
  onPointVisualizationMethodChange,
  onFullFidelityModeChange,
  settingLevel,
  values,
}: LinePlotPointAggregationProps) => {
  // Generate stable and unique IDs
  const ref = useRef({
    samplingRadioId: uuidv4() + 'point-visualization-sampling',
    bucketingRadioId: uuidv4() + 'point-visualization-bucketing-gorilla',
  });

  const isFullFidelitySelected =
    displayValues.pointVisualizationMethod ===
    POINT_VISUALIZATION_OPTIONS.BucketingGorilla;
  const showFullFidelityDisplay =
    onFullFidelityModeChange != null && isFullFidelitySelected;

  const clearSettingRows = useClearSettingRows({
    numOverrides,
    settingKey: 'pointVisualizationMethod',
    settingLevel,
    clearSetting,
    emptySettings: {
      pointVisualizationMethod: undefined,
      fullFidelityMode: undefined,
    },
  });

  const radioValue: RadioValue = displayValues.pointVisualizationMethod;

  const handleRadioChange = (next: RadioValue) => {
    if (next !== displayValues.pointVisualizationMethod) {
      onPointVisualizationMethodChange(next);
    }
  };

  const fullFidelityDisplayOption = FULL_FIDELITY_DISPLAY_OPTIONS.find(
    option =>
      option.value ===
      (displayValues.fullFidelityMode ?? FULL_FIDELITY_MODES.Standard)
  );

  return (
    <fieldset className={twMerge('text-wb-text-primary', className)}>
      <div className="flex items-center justify-between">
        <div className="center mb-4 flex flex-col">
          <div className="flex items-center gap-4">
            <SettingLabel className="w-fit" text="Point aggregation method" />
            <Button
              size="small"
              variant="ghost"
              icon="info"
              tooltipProps={{className: 'w-[20rem]'}}
              tooltip={
                <>
                  <div className="mb-12">
                    <strong>Random sampling</strong> displays a subset of points
                    to improve performance with large datasets.
                  </div>
                  <div>
                    <strong>Full fidelity</strong> shows all data points with
                    point aggregation across x-axis buckets.
                  </div>
                </>
              }
            />
          </div>
          <p className="text-sm text-wb-text-tertiary">
            Only applies to lines with more than 1,000 data points.
          </p>
        </div>
        <ResetSettingButton
          isEnabled={arePointAggregationSettingsActive(values)}
          onButtonClick={() =>
            clearSetting?.(settingLevel, 'pointVisualizationMethod', {
              pointVisualizationMethod: undefined,
              fullFidelityMode: undefined,
            })
          }
          settingLevel={settingLevel}
        />
      </div>
      <Radio.Root
        aria-label="Point aggregation method"
        name="point-aggregation-method"
        onValueChange={value => {
          if (isRadioValue(value)) {
            handleRadioChange(value);
          }
        }}
        value={radioValue}>
        <div
          className="flex items-center justify-start"
          data-test="point-aggregation-method-sampling">
          <Radio.Item
            // oxlint-disable-next-line react/refs
            id={ref.current.samplingRadioId}
            value={POINT_VISUALIZATION_OPTIONS.Sampling}>
            <Radio.Indicator />
          </Radio.Item>
          {/* oxlint-disable-next-line react/refs */}
          <label htmlFor={ref.current.samplingRadioId}>
            <span className="mx-8">Random sampling</span>
          </label>
        </div>
        <div
          className="flex items-center justify-start"
          data-test="point-aggregation-method-full-fidelity">
          <Radio.Item
            // oxlint-disable-next-line react/refs
            id={ref.current.bucketingRadioId}
            value={POINT_VISUALIZATION_OPTIONS.BucketingGorilla}>
            <Radio.Indicator />
          </Radio.Item>
          {/* oxlint-disable-next-line react/refs */}
          <label htmlFor={ref.current.bucketingRadioId}>
            <span className="mx-8">Full fidelity</span>
          </label>
        </div>
      </Radio.Root>
      {showFullFidelityDisplay && (
        <div
          className="mt-12 flex flex-col gap-y-8"
          data-test="full-fidelity-display">
          <SettingLabel className="w-fit" text="Full fidelity display" />
          <SettingSelect
            ddActionName="Full fidelity display"
            className="w-full"
            options={FULL_FIDELITY_DISPLAY_OPTIONS}
            value={fullFidelityDisplayOption}
            onChange={selected => {
              if (selected != null) {
                onFullFidelityModeChange?.(selected.value);
              }
            }}
          />
        </div>
      )}
      <ClearSettingsRows rows={clearSettingRows} />
    </fieldset>
  );
};
